import { ApiErrorHandler } from './api-error-handler-factory';
import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { ServerSideErrorStatusDict } from '../../../../../shared/enums/server-side-statuses.dict';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { AuthService } from '../../../auth/auth.service';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root',
})
export class RegularApiErrorHandler implements ApiErrorHandler {
  private messageService = inject(MessageService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private transloco = inject(TranslocoService);
  private readonly userNotFoundMessage = 'dict.auth.user_not_found';
  private readonly phoneLoginOtpUrl =
    '/corporate-client/v2/login/phone/send-otp';
  private readonly passwordRecoveryUrls = [
    '/corporate-client/auth/reset',
    '/corporate-client/password/forgot',
  ];
  private readonly silentErrorUrls = [
    '/api/v2/inbox',
    '/api/v2/notification-settings',
  ];

  prepareRequest(request: HttpRequest<any>): HttpRequest<any> {
    return request;
  }

  handleError(apiError: HttpErrorResponse): void {
    const backend = apiError?.error;
    const isPasswordRecovery = this.passwordRecoveryUrls.some((url) =>
      apiError.url?.split('?')[0].endsWith(url)
    );
    const errorMessage =
      backend?.message ??
      (isPasswordRecovery ? backend?.data?.message : undefined);
    const hasValidationErrors =
      backend?.errors && typeof backend.errors === 'object';

    if (this.shouldHandleSilently(apiError)) {
      return;
    }

    switch (apiError.status) {
      case ServerSideErrorStatusDict.BAD_REQUEST:
      case ServerSideErrorStatusDict.UNPROCESSABLE_ENTITY: {
        if (hasValidationErrors) {
          return;
        }

        this.messageService.add({
          severity: 'error',
          summary: this.authText('error', 'Ошибка'),
          detail:
            errorMessage ?? this.authText('defaultError', 'Произошла ошибка'),
          key: 'root',
        });

        break;
      }

      case ServerSideErrorStatusDict.NOT_FOUND: {
        if (
          backend?.message === this.userNotFoundMessage ||
          apiError.url?.includes(this.phoneLoginOtpUrl)
        ) {
          return;
        }

        this.messageService.add({
          severity: 'warn',
          summary: this.authText(
            'notifications.unavailableTitle',
            'Нет найдено!'
          ),
          detail: this.authText(
            'notifications.unavailable',
            'К сожалению, этот ресурс недоступен'
          ),
          key: 'root',
        });
        break;
      }

      case ServerSideErrorStatusDict.FORBIDDEN: {
        break;
      }

      case ServerSideErrorStatusDict.ITERNAL_SERVER_ERROR: {
        this.messageService.add({
          severity: 'warn',
          summary: this.authText(
            'notifications.unavailableTitle',
            'Внутреняя ошибка сервера'
          ),
          detail: this.authText(
            'notifications.unavailable',
            'К сожалению, функционал недоступен по техническим причинам'
          ),
          key: 'root',
        });
        break;
      }

      case ServerSideErrorStatusDict.BAD_GATEWAY: {
        this.messageService.add({
          severity: 'warn',
          summary: this.authText('error', 'Ошибка'),
          detail: this.authText(
            'notifications.connection',
            'К сожалению, сервер недоступен. Возможно из-за проблем с соединением'
          ),
          key: 'root',
        });
        break;
      }

      case ServerSideErrorStatusDict.SERVICE_UNAVAILABLE: {
        this.messageService.add({
          severity: 'warn',
          summary: this.authText('error', 'Ошибка'),
          detail: this.authText(
            'notifications.unavailable',
            'К сожалению, сервер недоступен по техническим причинам'
          ),
          key: 'root',
        });
        break;
      }

      case ServerSideErrorStatusDict.GATEWAY_TIMEOUT: {
        this.messageService.add({
          severity: 'warn',
          summary: this.authText('error', 'Ошибка'),
          detail: this.authText(
            'notifications.connection',
            'К сожалению, сервер недоступен. Возможно из-за проблем с соединением'
          ),
          key: 'root',
        });
        break;
      }

      case ServerSideErrorStatusDict.UNAUTHORIZED: {
        this.messageService.add({
          severity: 'error',
          summary: this.authText('error', 'Ошибка'),
          detail: this.authText(
            'notifications.signInRequired',
            'Для продолжения необходимо авторизоваться'
          ),
          key: 'root',
        });
        this.authService.logout();
        break;
      }

      default: {
        this.messageService.add({
          severity: 'error',
          summary: this.authText('error', 'Ошибка'),
          detail: this.authText('defaultError', 'Техническая ошибка'),
          key: 'root',
        });
        break;
      }
    }
  }

  private authText(key: string, fallback: string): string {
    if (!/^\/auth(?:\/|[?#]|$)/.test(this.router.url)) return fallback;

    const translationKey = `auth.${key}`;
    const text = this.transloco.translate<string>(translationKey);
    return text === translationKey ? fallback : text;
  }

  private shouldHandleSilently(apiError: HttpErrorResponse): boolean {
    // This form displays API and validation errors inline; keep session expiry handling.
    if (
      apiError.status !== ServerSideErrorStatusDict.UNAUTHORIZED &&
      apiError.url?.split('?')[0].endsWith('/corporate-client/password/set')
    ) {
      return true;
    }

    return this.silentErrorUrls.some((url) => apiError.url?.includes(url));
  }
}
